import React, { useCallback, useState, Fragment } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Form, ToggleButtonGroup, ToggleButton, Card, Collapse } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import moment from 'moment';

import { timeFormat } from '../../../constants';

import FormModal from '../../../components/FormModal';

import SingleDatePicker from '../../../components/form/SingleDatePicker';
import { getTimezoneOffset } from '../../../components/values/Timezone';
import { getDateTimeMoment } from '../../../lib/utils';

import { recalibrate } from '../actions';
import { getDevice } from '../selectors';

function RelearnModalForm({
  device = {},
  recalibrate,
  showPreviousValue = false,
  initialDisplayOption = showPreviousValue ? 'previous': 'now',
  initialValue = showPreviousValue ? device.calibration_start : undefined,
  learningStart,
  runningCutoff,
  children,
  ...props
}) {

  const { t } = useTranslation();
  const initialMoment = moment(initialValue);
  const [displayOption, setDisplayOption] = useState(initialDisplayOption);
  const [valid, setValid] = useState(true);
  const [advanced, setAdvanced] = useState(!!learningStart);
  const showDropdown = displayOption === initialDisplayOption;
  const showAdvanced = advanced || !showDropdown;
  const toggleAdvanced = () => showDropdown && setAdvanced(!advanced);

  const [state, setState] = useState(() => {
    const customMoment = learningStart && moment(learningStart);
    return {
      // use absolute start of days
      dateMoment: (customMoment || initialMoment).clone().startOf('day'),
      // need to be able to keep invalid dates in the field
      timeString: (customMoment || initialMoment).format(timeFormat),
    };
  });

  const handleSubmit = useCallback(e => {
    e.preventDefault();
    if (valid) {
      const dateTimeMoment = getDateTimeMoment(state.dateMoment, state.timeString);
      return recalibrate(device, {
        fitmachine_onboard_date: dateTimeMoment.toISOString(),
        rms_running_cutoff: runningCutoff,
      }, e); // pass event for advanced usage
    }
  }, [device, state.dateMoment, state.timeString, valid]);

  const handleDisplayOption = useCallback(value => {
    // set toggle status
    setDisplayOption(value);
    // set the time
    if (value !== 'custom') {
      const nextMoment = value === 'previous' ? initialMoment : moment();
      setState({
        dateMoment: nextMoment.clone().startOf('day'),
        timeString: nextMoment.format(timeFormat),
      });
    }
  }, []);

  const dateTimeMoment = getDateTimeMoment(state.dateMoment, state.timeString);
  return (
    <FormModal
      // set defaults
      header={t('components.equipment.relearn-modal-form.header')}
      body={t('components.equipment.relearn-modal-form.body', {
        equipmentName: device.equipment_name
      })}
      confirmText={t('components.equipment.relearn-modal-form.confirm-text')}
      // add given props
      {...props}
      // override given props
      size="md"
      valid={valid}
      form={(
        <Form onSubmit={handleSubmit}>
          <Card className="original-card shadow-sm" style={{ marginTop: '-1rem' }}>
            <Card.Header
              onClick={toggleAdvanced}
              className={`drop${showAdvanced ? 'up' : 'down'}`}
              // declare accessibility attributes as given in examples given in
              // https://getbootstrap.com/docs/4.2/components/dropdowns/
              data-toggle="dropdown"
              aria-haspopup="false"
              aria-expanded={showAdvanced}
              style={showDropdown ? { cursor: 'pointer' } : undefined}
            >
              {t('components.equipment.relearn-modal-form.advanced-options')}
              {showDropdown && (
                <span className="dropdown-toggle float-right"></span>
              )}
            </Card.Header>
            <Collapse in={showAdvanced}>
              <Card.Body>
                <Form.Group as={Row} controlId="fitmachine_onboard_option">
                  <Form.Label column sm="5">
                    {t('components.equipment.relearn-modal-form.learning-start')}
                  </Form.Label>
                  <Col sm="7">
                    <ToggleButtonGroup
                      type="radio"
                      name="Learning Date Type"
                      value={displayOption}
                      onChange={handleDisplayOption}
                    >
                      {showPreviousValue && device.calibration_start && (
                        <ToggleButton value="previous">
                          {t('components.equipment.relearn-modal-form.unchanged')}
                        </ToggleButton>
                      )}
                      <ToggleButton value="now">
                        {t('components.equipment.relearn-modal-form.now')}
                      </ToggleButton>
                      <ToggleButton value="custom">
                        {t('components.equipment.relearn-modal-form.custom')}
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Col>
                </Form.Group>
                {displayOption === 'custom' && (
                  <Fragment>
                    <Form.Group as={Row} controlId="fitmachine_onboard_date">
                      <Form.Label column sm="5">
                        {t('components.equipment.relearn-modal-form.learning-start-date')}
                      </Form.Label>
                      <Col sm="7">
                        <SingleDatePicker
                          key={`fitmachine_onboard_date-${state.dateMoment.toISOString()}`}
                          date={state.dateMoment}
                          onDateChange={dateMoment => {
                            const { timeString } = state;
                            const newDateTime = getDateTimeMoment(dateMoment, timeString);
                            setState({
                              ...state,
                              dateMoment: dateMoment.clone().startOf('day'),
                            });
                            setValid(newDateTime && !newDateTime.isAfter());
                          }}
                          showDefaultInputIcon={true}
                          id="fitmachine_onboard_date"
                          placeholder={t('components.equipment.relearn-modal-form.learning-date')}
                        />
                      </Col>
                    </Form.Group>
                    <Form.Group as={Row} controlId="fitmachine_onboard_time">
                      <Form.Label column sm="5">
                        {t('components.equipment.relearn-modal-form.learning-start-time')}&nbsp;
                        ({getTimezoneOffset()})
                      </Form.Label>
                      <Col sm="7">
                        <Form.Control
                          type="text"
                          name="fitmachine_onboard_time"
                          isInvalid={!valid}
                          value={state.timeString}
                          onChange={e => {
                            const { dateMoment } = state;
                            const timeString = e.target.value;
                            const dateTimeMoment = getDateTimeMoment(dateMoment, timeString);
                            setState({ ...state, timeString });
                            setValid(dateTimeMoment && !dateTimeMoment.isAfter());
                          }}
                        />
                      </Col>
                    </Form.Group>
                  </Fragment>
                )}
              </Card.Body>
            </Collapse>
          </Card>
          <br/>
          {dateTimeMoment && (
            <Form.Group>
              <div>{t('components.equipment.relearn-modal-form.trigger-relearning', {
                dateTime: dateTimeMoment.local().format("LLLL"),
                offset: getTimezoneOffset(),
              })}</div>
            </Form.Group>
          )}
        </Form>
      )}
    >
      {children}
    </FormModal>
  );
}

const mapStateToProps = (state, { deviceId }) => ({
  device: getDevice(state, deviceId),
});
const mapDispatchToProps = { recalibrate };
// use recalibrate from actions (dispatchProps), but let it be overridden by ownProps if specified
const mergeProps = (stateProps, dispatchProps, ownProps) => ({
  ...stateProps,
  ...dispatchProps,
  ...ownProps,
});

export default connect(mapStateToProps, mapDispatchToProps, mergeProps)(RelearnModalForm);
